"use client";
import React from "react";
import { Link } from "@/navigation";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { getBlogList } from "@/lib/api/blog";
import type { Blog } from "@/lib/@types/api/blog";
import clsx from "clsx";

interface ApplicationsBlogDetailProps {
	blog: Blog.BlogContent;
	secondaryCategories: Blog.BlogCl[];
	relatedBlogs: Blog.BlogListItem[];
	locale: string;
	activeSecondaryCategory: Blog.BlogCl;
	clsList: Blog.BlogCl[];
}

// 左侧博客列表项组件 - 使用 memo 防止不必要的重新渲染
const BlogListItem = React.memo(({ 
	item, 
	currentSlug 
}: { 
	item: Blog.BlogListItem; 
	currentSlug: string;
}) => {
	const isCurrent = item.blog_slug === currentSlug;
	return (
		<Link
			href={`/blog/${item.blog_slug}`}
			className={clsx(
				"flex items-center justify-between hover:bg-mainColor/20 py-3 px-5 transition-all duration-200 border-b border-gray-200",
				"bg-white",
				isCurrent 
					? "!text-mainColor font-medium" 
					: "text-gray-600 hover:!text-mainColor"
			)}
		>
			<span className="flex items-start gap-3 flex-1">
				<span className={clsx(
					"text-base transition-colors duration-200 flex-shrink-0 mt-0.5",
					isCurrent ? "!text-mainColor" : "text-gray-400"
				)}>&gt;</span>
				<span className={clsx(
					"text-base break-words",
					isCurrent ? "font-medium" : "font-normal"
				)}>
					{item.blog_title}
				</span>
			</span>
		</Link>
	);
}, (prevProps, nextProps) => {
	// 只有当当前项的状态（是否是选中项）发生变化时才重新渲染
	const prevIsCurrent = prevProps.item.blog_slug === prevProps.currentSlug;
	const nextIsCurrent = nextProps.item.blog_slug === nextProps.currentSlug;
	
	// 如果当前项状态没变，且 item 数据没变，则不重新渲染
	return prevIsCurrent === nextIsCurrent && 
		   prevProps.item.blog_id === nextProps.item.blog_id &&
		   prevProps.item.blog_title === nextProps.item.blog_title;
});

BlogListItem.displayName = 'BlogListItem';

// 左侧分类列表项组件
const CategoryListItem = React.memo(({
	thirdCls,
	isExpanded,
	categoryBlogs,
	onToggle,
	currentBlogSlug
}: {
	thirdCls: Blog.BlogCl;
	isExpanded: boolean;
	categoryBlogs: Blog.BlogListItem[];
	onToggle: () => void;
	currentBlogSlug: string;
}) => {
	return (
		<div className="relative">
			{/* 一级菜单：三级分类名称 */}
			<div 
				className={clsx(
					"group flex items-center justify-between cursor-pointer py-3 px-5 transition-all duration-200 border border-gray-100",
					isExpanded ? "bg-mainColor" : "bg-mainColor/95"
				)}
				onClick={onToggle}
			>
				<span className={clsx(
					"flex-1 text-base md:text-lg font-medium transition-colors duration-200",
					"text-white"
				)}>
					{thirdCls.cls_name}
				</span>
				
				<button
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						onToggle();
					}}
					className="flex h-10 w-10 items-center justify-center text-white hover:text-white transition-colors ml-3 text-xl font-bold flex-shrink-0"
				>
					{isExpanded ? "-" : "+"}
				</button>
			</div>

			{/* 二级菜单：博客标题列表 */}
			<div 
				className={clsx(
					"overflow-hidden transition-all duration-500 ease-in-out",
					isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
				)}
				style={{
					transitionProperty: 'max-height, opacity',
					transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
				}}
			>
				<div className="bg-white">
					{categoryBlogs.length > 0 ? (
						categoryBlogs.map((item) => (
							<BlogListItem 
								key={item.blog_id}
								item={item}
								currentSlug={currentBlogSlug}
							/>
						))
					) : (
						<div className=""></div>
					)}
				</div>
			</div>
		</div>
	);
}, (prevProps, nextProps) => {
	return prevProps.isExpanded === nextProps.isExpanded &&
		   prevProps.currentBlogSlug === nextProps.currentBlogSlug &&
		   prevProps.categoryBlogs.length === nextProps.categoryBlogs.length;
});

CategoryListItem.displayName = 'CategoryListItem';

// 左侧分类列表组件 - 使用 memo 防止不必要的重新渲染
const CategoryList = React.memo(({
	thirdLevelCategories,
	blogsByCategory,
	expandedClsIds,
	setExpandedClsIds,
	currentBlogSlug
}: {
	thirdLevelCategories: Blog.BlogCl[];
	blogsByCategory: Record<number, Blog.BlogListItem[]>;
	expandedClsIds: number[];
	setExpandedClsIds: React.Dispatch<React.SetStateAction<number[]>>;
	currentBlogSlug: string;
}) => {
	// 创建切换函数映射
	const toggleFunctions = React.useMemo(() => {
		const map: Record<number, () => void> = {};
		thirdLevelCategories.forEach(thirdCls => {
			map[thirdCls.cls_id] = () => {
				setExpandedClsIds(prev => {
					if (prev.includes(thirdCls.cls_id)) {
						return prev.filter(id => id !== thirdCls.cls_id);
					} else {
						return [...prev, thirdCls.cls_id];
					}
				});
			};
		});
		return map;
	}, [thirdLevelCategories, setExpandedClsIds]);

	return (
		<div className="flex flex-col border border-gray-200 shadow-md">
			{thirdLevelCategories.map((thirdCls) => {
				const isExpanded = expandedClsIds.includes(thirdCls.cls_id);
				const categoryBlogs = blogsByCategory[thirdCls.cls_id] || [];
				const toggleExpanded = toggleFunctions[thirdCls.cls_id];

				return (
					<CategoryListItem
						key={thirdCls.cls_id}
						thirdCls={thirdCls}
						isExpanded={isExpanded}
						categoryBlogs={categoryBlogs}
						onToggle={toggleExpanded}
						currentBlogSlug={currentBlogSlug}
					/>
				);
			})}
		</div>
	);
}, (prevProps, nextProps) => {
	// 检查展开状态是否相同
	const expandedSame = prevProps.expandedClsIds.length === nextProps.expandedClsIds.length &&
		prevProps.expandedClsIds.every((id, idx) => id === nextProps.expandedClsIds[idx]);
	
	// 检查博客数据是否相同（通过比较每个分类的博客数量）
	const blogsSame = Object.keys(prevProps.blogsByCategory).every(key => {
		const prevBlogs = prevProps.blogsByCategory[Number(key)];
		const nextBlogs = nextProps.blogsByCategory[Number(key)];
		return prevBlogs?.length === nextBlogs?.length;
	}) && Object.keys(prevProps.blogsByCategory).length === Object.keys(nextProps.blogsByCategory).length;
	
	// 只有当展开状态、博客数据或当前选中项变化时才重新渲染
	return expandedSame && blogsSame && prevProps.currentBlogSlug === nextProps.currentBlogSlug;
});

CategoryList.displayName = 'CategoryList';

const ApplicationsBlogDetail = ({
	blog,
	secondaryCategories,
	relatedBlogs,
	locale,
	activeSecondaryCategory,
	clsList,
}: ApplicationsBlogDetailProps) => {
	const t = useTranslations();
	const [expandedClsIds, setExpandedClsIds] = React.useState<number[]>([]);
	const [blogsByCategory, setBlogsByCategory] = React.useState<Record<number, Blog.BlogListItem[]>>({});
	const [expandedTabs, setExpandedTabs] = React.useState<boolean[]>([false, false]);
	
	// 对二级分类进行排序（按 cls_id 排序，与 Applications 组件保持一致）
	const sortedSecondaryCategories = React.useMemo(() => {
		return [...secondaryCategories].sort((a, b) => {
			// 按 cls_id 数字排序
			return a.cls_id - b.cls_id;
		});
	}, [secondaryCategories]);
	
	// 获取当前二级分类下的所有三级分类
	const thirdLevelCategories = React.useMemo(() => {
		return clsList
			.filter(c => c.cls_parent === activeSecondaryCategory?.cls_id)
			.sort((a, b) => a.cls_id - b.cls_id);
	}, [clsList, activeSecondaryCategory]);

	// 找到当前博客所属的三级分类名称
	const thirdLevelCategoryName = React.useMemo(() => {
		// 1. 优先从当前二级分类下的子分类中找
		const currentThirdCls = blog.blog_classification_list.find(c =>
			thirdLevelCategories.some(t => t.cls_id === c.cls_id)
		);
		if (currentThirdCls) return currentThirdCls.cls_name;

		// 2. 其次找非二级分类的分类
		const otherCls = blog.blog_classification_list.find(
			(cls) => cls.cls_id !== activeSecondaryCategory?.cls_id
		);
		if (otherCls) return otherCls.cls_name;

		// 3. 兜底逻辑
		return blog.blog_classification_list[0]?.cls_name;
	}, [blog.blog_classification_list, thirdLevelCategories, activeSecondaryCategory]);

	// 使用 useMemo 稳定当前博客 slug，避免不必要的重新渲染
	const currentBlogSlug = React.useMemo(() => blog.slug, [blog.slug]);
	
	// 使用 useCallback 稳定 setExpandedClsIds 函数
	const handleSetExpandedClsIds = React.useCallback((updater: React.SetStateAction<number[]>) => {
		setExpandedClsIds(updater);
	}, []);
	
	// 使用 useMemo 稳定左侧列表的 props
	const categoryListProps = React.useMemo(() => ({
		thirdLevelCategories,
		blogsByCategory,
		expandedClsIds,
		setExpandedClsIds: handleSetExpandedClsIds,
		currentBlogSlug
	}), [thirdLevelCategories, blogsByCategory, expandedClsIds, handleSetExpandedClsIds, currentBlogSlug]);

	// 为每个三级分类获取对应的博客列表
	React.useEffect(() => {
		const fetchBlogsForCategories = async () => {
			if (!activeSecondaryCategory || thirdLevelCategories.length === 0) return;
			
			try {
				// 并行获取所有三级分类下的博客
				const promises = thirdLevelCategories.map(async (thirdCls) => {
					const res = await getBlogList({
						lang_code: { lang_code: locale },
						page: 1,
						limit: 50,
						cls_ids: [thirdCls.cls_id]
					});
					return {
						clsId: thirdCls.cls_id,
						blogs: res?.detail?.blog_list || []
					};
				});

				const results = await Promise.all(promises);
				const blogsMap: Record<number, Blog.BlogListItem[]> = {};
				results.forEach(({ clsId, blogs }) => {
					blogsMap[clsId] = blogs;
				});
				setBlogsByCategory(blogsMap);
			} catch (e) {
				console.error("Error fetching blogs:", e);
			}
		};
		
		fetchBlogsForCategories();
	}, [activeSecondaryCategory, thirdLevelCategories, locale]);

	// 设置初始展开的分类
	React.useEffect(() => {
		const currentBlogCls = blog.blog_classification_list.find(c => 
			thirdLevelCategories.some(t => t.cls_id === c.cls_id)
		);
		if (currentBlogCls) {
			setExpandedClsIds([currentBlogCls.cls_id]);
		} else if (thirdLevelCategories.length > 0) {
			setExpandedClsIds([thirdLevelCategories[0].cls_id]);
		}
	}, [thirdLevelCategories, blog.blog_classification_list]);

	return (
		<div className="bg-white min-h-screen">
			{/* 1. 顶部二级分类菜单 */}
			<div className="bg-mainColor/20 border-b pt-[75px] max-md:pt-0">
				<div className="container mx-auto">
					<div className="flex items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 flex-wrap py-6 md:py-8 lg:py-10">
						{sortedSecondaryCategories.map((cat) => {
							const isActive = activeSecondaryCategory?.cls_id === cat.cls_id;
							return (
								<Link
									key={cat.cls_id}
									href={`/applications?tab=${cat.cls_id}`}
									className={clsx(
										"relative text-sm sm:text-base md:text-lg font-medium transition-colors duration-300 pb-2 px-1",
										isActive ? "!text-mainColor" : "text-gray-700 hover:!text-mainColor"
									)}
								>
									{cat.cls_name}
									{isActive && (
										<span className="absolute left-0 right-0 bottom-0 h-[3px] bg-mainColor"></span>
									)}
								</Link>
							);
						})}
					</div>
				</div>
			</div>

			{/* 2. 面包屑 */}
			<div className="container mx-auto py-4">
				<div className="flex flex-wrap items-center lg:justify-end gap-x-2 gap-y-1 text-sm sm:text-base md:text-lg font-normal">
					<Link href="/" className=" text-black hover:text-mainColor flex items-center gap-1">
						<i className="ri-home-8-fill text-lg text-mainColor"></i>
						<span className="hover:underline">{t("menu.Home")}</span>
					</Link>
					<span className="text-gray-400">/</span>
					<Link href="/applications" className="text-black hover:text-mainColor hover:underline">
						{t("menu.applications")}
					</Link>
					<span className="text-gray-400">/</span>
					<Link href={`/applications?tab=${activeSecondaryCategory?.cls_id}`} className="text-black hover:text-mainColor hover:underline">
						{activeSecondaryCategory?.cls_name}
					</Link>
					<span className="text-gray-400">/</span>
					<span className="text-[#00529b] font-medium">
						{thirdLevelCategoryName}
					</span>
				</div>
			</div>

			{/* 3. 下半部分：左右布局 */}
			<div className="container mx-auto border-t border-gray-200">
				<div className="flex flex-col lg:flex-row gap-0">
					{/* 左侧：博客列表 - 手风琴模式 */}
					<div className="w-full lg:w-[20%] flex-shrink-0 border-r border-gray-200 bg-white">
						<CategoryList {...categoryListProps} />
					</div>

					{/* 右侧：详情 */}
					<div className="flex-1 pl-8 py-8 max-md:py-5 max-md:px-0">
							<div className="mb-10">
								<h1 className="text-xl md:text-3xl font-medium text-gray-800 leading-tight mb-10 max-md:mb-5">
									{blog.blog_title}
								</h1>
								
								<div className="relative w-full aspect-[16/9] overflow-hidden">
									<Image
										src={blog.cover_url}
										alt={blog.blog_title}
										fill
										className="object-cover"
										priority
										sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
									/>
								</div>
							</div>
						
							{/* 正文内容区域 */}
							<div className="mt-10">
								{/* 控制按钮 */}
								<div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 mb-6">
									<button
										onClick={() => setExpandedTabs([true, true])}
										className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-mainColor text-mainColor hover:bg-mainColor hover:text-white transition-all duration-200 rounded text-sm sm:text-base"
									>
										<span>{t("nav.Open-All")}</span>
										<i className="ri-arrow-down-s-line"></i>
									</button>
									<div className="hidden sm:block w-px h-6 bg-gray-300"></div>
									<button
										onClick={() => setExpandedTabs([false, false])}
										className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-mainColor text-mainColor hover:bg-mainColor hover:text-white transition-all duration-200 rounded text-sm sm:text-base"
									>
										<span>{t("nav.Close-All")}</span>
										<i className="ri-arrow-up-s-line"></i>
									</button>
								</div>

								{/* Tab 1: 系统解决方案 */}
								<div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
									<div 
										className={clsx(
											"flex items-center justify-between cursor-pointer py-4 px-5 transition-colors duration-200",
											expandedTabs[0] 
												? "bg-mainColor text-white" 
												: "bg-white hover:bg-gray-50"
										)}
										onClick={() => setExpandedTabs(prev => [!prev[0], prev[1]])}
									>
										<div className="flex items-center gap-3">
											<i className={clsx(
												"ri-settings-3-line text-xl transition-colors duration-200",
												expandedTabs[0] ? "text-white" : "text-mainColor"
											)}></i>
											<h2 className={clsx(
												"text-lg max-md:text-base font-bold transition-colors duration-200",
												expandedTabs[0] ? "text-white" : "text-gray-800"
											)}>{t("nav.System-Solutions")}</h2>
										</div>
										<i className={clsx(
											"ri-arrow-down-s-line text-xl transition-all duration-300",
											expandedTabs[0] 
												? "rotate-180 text-white" 
												: "text-mainColor"
										)}></i>
									</div>
									<div 
										className={clsx(
											"overflow-hidden transition-all duration-500 ease-in-out",
											expandedTabs[0] ? "max-h-[10000px] opacity-100" : "max-h-0 opacity-0"
										)}
									>
										<div className="p-5">
											<style jsx global>{`
												.blog-content-wrapper {
													color: #374151;
													line-height: 1.75;
													font-size: 1rem;
												}
												.blog-content-wrapper > * {
													margin-top: 1.5rem;
													margin-bottom: 1.5rem;
												}
												.blog-content-wrapper > *:first-child {
													margin-top: 0;
												}
												.blog-content-wrapper > *:last-child {
													margin-bottom: 0;
												}
												.blog-content-wrapper table {
													width: 100%;
													min-width: 100%;
													border-collapse: collapse;
													background-color: white;
													margin: 2rem 0;
													border: 1px solid #d1d5db;
												}
												.blog-content-wrapper table thead {
													background-color: #f5f5f5;
												}
												.blog-content-wrapper table thead th {
													padding: 0.75rem 1rem;
													text-align: center;
													font-weight: 500;
													font-size: 0.875rem;
													color: #1f2937;
													border: 1px solid #d1d5db;
												}
												.blog-content-wrapper table tbody tr {
													transition: background-color 0.15s ease;
												}
												.blog-content-wrapper table tbody tr:hover {
													background-color: rgba(0, 82, 155, 0.05) !important;
												}
												.blog-content-wrapper table tbody td {
													padding: 0.75rem 1rem;
													font-size: 0.875rem;
													color: #374151;
													line-height: 1.5;
													word-break: break-word;
													border: 1px solid #d1d5db;
													vertical-align: middle;
													text-align: left;
												}
												.blog-content-wrapper table tbody td a {
													display: inline-flex;
													align-items: center;
													gap: 0.25rem;
													white-space: nowrap;
													vertical-align: middle;
												}
												.blog-content-wrapper table tbody td span {
													display: inline-flex;
													align-items: center;
													gap: 0.25rem;
													white-space: nowrap;
													vertical-align: middle;
												}
												.blog-content-wrapper table tbody td img {
													display: inline-block;
													vertical-align: middle;
													margin: 0;
													flex-shrink: 0;
												}
												.blog-content-wrapper table tbody td i {
													display: inline-block;
													vertical-align: middle;
													margin: 0;
													flex-shrink: 0;
												}
												.blog-content-wrapper table tbody tr:nth-child(1) {
													background-color: #ffffff;
												}
												.blog-content-wrapper table tbody tr:nth-child(2) {
													background-color: #f9fafb;
												}
												.blog-content-wrapper table tbody tr:nth-child(3) {
													background-color: #f3f4f6;
												}
												.blog-content-wrapper table tbody tr:nth-child(4) {
													background-color: #ffffff;
												}
												.blog-content-wrapper table tbody tr:nth-child(5) {
													background-color: #f9fafb;
												}
												.blog-content-wrapper table tbody tr:nth-child(6) {
													background-color: #f3f4f6;
												}
												.blog-content-wrapper table tbody tr:nth-child(7) {
													background-color: #ffffff;
												}
												.blog-content-wrapper table tbody tr:nth-child(8) {
													background-color: #f9fafb;
												}
												.blog-content-wrapper table tbody tr:nth-child(9) {
													background-color: #f3f4f6;
												}
												.blog-content-wrapper table tbody tr:nth-child(10) {
													background-color: #ffffff;
												}
												.blog-content-wrapper table tbody tr:nth-child(11) {
													background-color: #f9fafb;
												}
												.blog-content-wrapper table tbody tr:nth-child(12) {
													background-color: #f3f4f6;
												}
												.blog-content-wrapper table tbody tr:nth-child(13) {
													background-color: #ffffff;
												}
												.blog-content-wrapper table tbody tr:nth-child(14) {
													background-color: #f9fafb;
												}
												.blog-content-wrapper table tbody tr:nth-child(15) {
													background-color: #f3f4f6;
												}
												.blog-content-wrapper table tbody tr:nth-child(16) {
													background-color: #ffffff;
												}
												.blog-content-wrapper table tbody tr:nth-child(17) {
													background-color: #f9fafb;
												}
												.blog-content-wrapper table tbody tr:nth-child(18) {
													background-color: #f3f4f6;
												}
												.blog-content-wrapper table tbody tr:nth-child(19) {
													background-color: #ffffff;
												}
												.blog-content-wrapper table tbody tr:nth-child(20) {
													background-color: #f9fafb;
												}
												.blog-content-wrapper p {
													margin: 1rem 0;
													line-height: 1.75;
												}
												.blog-content-wrapper img {
													max-width: 100%;
													height: auto;
													border-radius: 0.5rem;
													margin: 1.5rem 0;
												}
												.blog-content-wrapper h1,
												.blog-content-wrapper h2,
												.blog-content-wrapper h3,
												.blog-content-wrapper h4 {
													margin-top: 1.5rem;
													margin-bottom: 1rem;
													font-weight: 600;
													color: #1f2937;
												}
												.blog-content-wrapper ul,
												.blog-content-wrapper ol {
													margin: 1rem 0;
													padding-left: 1.5rem;
												}
												.blog-content-wrapper li {
													margin: 0.5rem 0;
													line-height: 1.75;
												}
												@media (max-width: 768px) {
													.blog-content-wrapper table {
														font-size: 0.75rem;
														margin: 1.5rem 0;
														min-width: 600px; /* 强制表格最小宽度，防止列被挤压成垂直文字 */
													}
													.blog-content-wrapper table thead th,
													.blog-content-wrapper table tbody td {
														padding: 0.5rem 0.75rem;
														word-break: normal; /* 移动端禁止强制断字，保持文字水平排列 */
													}
												}
											`}</style>
											<div 
												className="text-gray-700 leading-relaxed text-base blog-content-wrapper overflow-x-auto break-words"
												dangerouslySetInnerHTML={{ __html: blog.blog_content }}
											/>
										</div>
									</div>
								</div>

								{/* Tab 2: 产品优势 */}
								<div className="border border-gray-200 rounded-lg overflow-hidden">
									<div 
										className={clsx(
											"flex items-center justify-between cursor-pointer py-4 px-5 transition-colors duration-200",
											expandedTabs[1] 
												? "bg-mainColor text-white" 
												: "bg-white hover:bg-gray-50"
										)}
										onClick={() => setExpandedTabs(prev => [prev[0], !prev[1]])}
									>
										<div className="flex items-center gap-3">
											<i className={clsx(
												"ri-award-line text-xl transition-colors duration-200",
												expandedTabs[1] ? "text-white" : "text-mainColor"
											)}></i>
											<h2 className={clsx(
												"text-lg max-md:text-base font-bold transition-colors duration-200",
												expandedTabs[1] ? "text-white" : "text-gray-800"
											)}>{t("nav.Product-Advantages")}</h2>
										</div>
										<i className={clsx(
											"ri-arrow-down-s-line text-xl transition-all duration-300",
											expandedTabs[1] 
												? "rotate-180 text-white" 
												: "text-mainColor"
										)}></i>
									</div>
									<div 
										className={clsx(
											"overflow-hidden transition-all duration-500 ease-in-out",
											expandedTabs[1] ? "max-h-[10000px] opacity-100" : "max-h-0 opacity-0"
										)}
									>
										<div className="p-5">
											<div 
												className="text-gray-700 leading-relaxed text-base blog-content-wrapper overflow-x-auto break-words"
												style={{ whiteSpace: 'pre-wrap' }}
												dangerouslySetInnerHTML={{ __html: blog.blog_excerpt || t("nav.No-Product-Advantages-Description") }}
											/>
										</div>
									</div>
								</div>
							</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ApplicationsBlogDetail;

